/**
 * Niederschlagsradar von MeteoSchweiz über der Karte.
 *
 * Die Bilder entstehen auf unserem Server (deploy/radar-update.py) alle fünf
 * Minuten aus den offenen RZC-Rohdaten und liegen unter /radar/. Hier wird nur
 * noch das jüngste Bild - oder die Schleife der letzten Stunde - als Bildquelle
 * über die Karte gelegt.
 *
 * Eine Bildquelle statt Kacheln: das Komposit ist ein einziges 1-km-Gitter über
 * den ganzen Alpenraum, da lohnt sich kein Kachelsatz. Weil das Bild nach Norden
 * ausgerichtet in Web-Mercator vorliegt, sind die vier Eckpunkte exakt - MapLibre
 * interpoliert zwischen ihnen in genau dieser Projektion.
 */
const BASE = '/radar';
const SOURCE = 'radar';
const LAYER = 'radar-layer';
/** Darüber beginnt die Beschriftung - die soll oben bleiben. */
const BEFORE = 'label-water';

const pad = (n) => String(n).padStart(2, '0');

/** "20261002T1950" -> lokale Zeit */
function readStamp(key) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})$/.exec(key || '');
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi));
}

function formatStamp(key) {
  const t = readStamp(key);
  if (!t) return '–';
  const mins = Math.round((Date.now() - t.getTime()) / 60000);
  const clock = `${pad(t.getHours())}:${pad(t.getMinutes())}`;
  if (mins < 1) return `${clock} Uhr, soeben`;
  if (mins < 60) return `${clock} Uhr, vor ${mins} min`;
  return `${clock} Uhr`;
}

export function createRadar({ map }) {
  let index = null;         // Inhalt von /radar/index.json
  let on = false;
  let pos = -1;             // Bildnummer, -1 = jüngstes
  let playing = false;
  let playTimer = null;
  let pollTimer = null;
  const nodes = {};

  const frames = () => index?.bilder || [];
  const currentKey = () => {
    const f = frames();
    if (!f.length) return null;
    return f[pos < 0 ? f.length - 1 : Math.min(pos, f.length - 1)];
  };

  async function loadIndex() {
    try {
      // Umgehungszwischenspeicher: das Bild wechselt alle fünf Minuten.
      const r = await fetch(`${BASE}/index.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(String(r.status));
      index = await r.json();
      return true;
    } catch {
      index = null;
      return false;
    }
  }

  /** Quelle und Ebene anlegen - auch nach einem Stilwechsel wieder. */
  function attach() {
    const key = currentKey();
    if (!on || !key || !index?.ecken) return;
    if (!map.getSource(SOURCE)) {
      map.addSource(SOURCE, {
        type: 'image',
        url: `${BASE}/frames/${key}.png`,
        coordinates: index.ecken
      });
    } else {
      map.getSource(SOURCE).updateImage({ url: `${BASE}/frames/${key}.png` });
    }
    if (!map.getLayer(LAYER)) {
      map.addLayer({
        id: LAYER,
        type: 'raster',
        source: SOURCE,
        paint: { 'raster-opacity': 0.82, 'raster-fade-duration': 0, 'raster-resampling': 'nearest' }
      }, map.getLayer(BEFORE) ? BEFORE : undefined);
    }
    showCredit(true);
  }

  /**
 * CC-BY verlangt die Nennung. Eine Bildquelle nimmt in MapLibre kein
 * `attribution` - der Stil lehnt die Quelle sonst ganz ab. Darum wird der
 * Hinweis direkt in die Quellenangabe gehaengt, solange das Radar laeuft.
 */
const CREDIT_ID = 'radar-credit';
function showCredit(show) {
  const host = document.querySelector('.maplibregl-ctrl-attrib-inner');
  if (!host) return;
  const existing = host.querySelector(`#${CREDIT_ID}`);
  if (show && !existing) {
    const span = document.createElement('span');
    span.id = CREDIT_ID;
    span.innerHTML = ' | <a href="https://www.meteoschweiz.admin.ch" target="_blank" rel="noopener">MeteoSchweiz</a>';
    host.append(span);
  } else if (!show && existing) {
    existing.remove();
  }
}

function detach() {
    if (map.getLayer(LAYER)) map.removeLayer(LAYER);
    if (map.getSource(SOURCE)) map.removeSource(SOURCE);
  }

  function stopPlaying() {
    playing = false;
    clearInterval(playTimer);
    playTimer = null;
  }

  function startPlaying() {
    if (frames().length < 2) return;
    playing = true;
    pos = 0;
    clearInterval(playTimer);
    playTimer = setInterval(() => {
      pos += 1;
      if (pos >= frames().length) pos = 0;
      attach();
      render();
    }, 550);
  }

  async function setOn(next) {
    on = next;
    if (on) {
      if (!index) await loadIndex();
      if (!index) { on = false; render(); return; }
      pos = -1;
      attach();
      // Alle zwei Minuten nachsehen, ob ein neueres Bild vorliegt.
      clearInterval(pollTimer);
      pollTimer = setInterval(async () => {
        const before = index?.aktuell;
        await loadIndex();
        if (index?.aktuell !== before && !playing) { pos = -1; attach(); }
        render();
      }, 120000);
    } else {
      stopPlaying();
      clearInterval(pollTimer);
      pollTimer = null;
      detach();
    }
    render();
  }

  function render() {
    if (!nodes.root) return;
    nodes.toggle.checked = on;
    nodes.body.hidden = !on;
    if (!on) return;
    const f = frames();
    nodes.time.textContent = index ? formatStamp(currentKey()) : 'nicht erreichbar';
    nodes.slider.max = String(Math.max(0, f.length - 1));
    nodes.slider.value = String(pos < 0 ? Math.max(0, f.length - 1) : pos);
    nodes.slider.disabled = f.length < 2;
    nodes.play.textContent = playing ? 'Anhalten' : 'Ablauf';
    nodes.play.disabled = f.length < 2;
  }

  function mount(host) {
    if (!host) return;
    host.textContent = '';
    host.classList.add('radar');

    const row = document.createElement('label');
    row.className = 'check';
    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    const text = document.createElement('span');
    text.textContent = 'Niederschlagsradar';
    const note = document.createElement('em');
    note.className = 'check__note';
    note.textContent = 'MeteoSchweiz';
    row.append(toggle, text, note);

    const body = document.createElement('div');
    body.className = 'radar__body';
    body.hidden = true;

    const time = document.createElement('div');
    time.className = 'radar__time';

    const controls = document.createElement('div');
    controls.className = 'radar__controls';
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.step = '1';
    slider.setAttribute('aria-label', 'Zeitpunkt');
    const play = document.createElement('button');
    play.type = 'button';
    play.className = 'linkbtn';
    controls.append(slider, play);

    const legend = document.createElement('div');
    legend.className = 'radar__legend';
    for (const [mm, rgb] of [[0.4, [102, 176, 230]], [1.6, [60, 160, 110]],
                             [6.3, [225, 225, 60]], [25, [235, 95, 40]], [63, [175, 30, 85]]]) {
      const cell = document.createElement('span');
      cell.style.background = `rgb(${rgb.join(',')})`;
      cell.title = `bis ${mm} mm/h`;
      legend.append(cell);
    }
    const legendText = document.createElement('span');
    legendText.className = 'radar__legend-text';
    legendText.textContent = 'mm/h';
    legend.append(legendText);

    body.append(time, controls, legend);
    host.append(row, body);
    Object.assign(nodes, { root: host, toggle, body, time, slider, play });

    toggle.addEventListener('change', () => setOn(toggle.checked));
    slider.addEventListener('input', () => {
      stopPlaying();
      pos = Number(slider.value);
      attach();
      render();
    });
    play.addEventListener('click', () => {
      if (playing) stopPlaying(); else startPlaying();
      render();
    });

    render();
  }

  return {
    mount,
    /** Nach einem Stilwechsel sind eigene Ebenen weg - wieder auftragen. */
    reapply: () => { if (on) attach(); },
    isOn: () => on
  };
}
